/**
 * IframeGatewayPage — /tracking/iframe-gateway (feature #163).
 *
 * Operator surface for configuring iframe-based conversion tracking
 * fallbacks for walled-garden advertiser pages. The backend lives at
 * /api/v1/iframe-gateway; this page:
 *
 *   1. Scoping input — operator types an advertiser ID to scope the view.
 *      An empty string = list all tenant gateways.
 *   2. Create form — name, slug, advertiser_id, allowed_origins (newline or
 *      comma separated), event type. Client-side validation mirrors the
 *      server regex so bad drafts never leave the browser.
 *   3. Gateways table — rows sorted active → paused → archived, each row
 *      shows slug, advertiser, event type, origin count, embed/conversion
 *      counters, conversion rate, last fired, status badge, and actions:
 *      copy embed snippet, pause/activate, delete (soft-archives when the
 *      gateway has fired).
 *   4. Embed snippet drawer — when a row is selected the page shows the
 *      ready-to-paste <iframe> markup pointing at the backend embed URL.
 *
 * Brand: emerald -> sky gradient headline, Inter, zinc borders, 6-8 px
 * rounded surfaces, light theme only. No emojis, no competitor names.
 *
 * Mirrors the "self-contained, no react-query" pattern used by AdvertiserCaps
 * / AdvertiserSubUsers / PublisherTrackingDomains so tests can drive a
 * react-dom/server smoke without pulling a DOM lib.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
	countByStatus,
	createGateway,
	deleteGateway,
	embedSnippet,
	embedUrl,
	EMPTY_DRAFT,
	EVENT_TYPE_LABELS,
	EVENT_TYPES,
	formatConversionRate,
	formatTimestamp,
	gatewayDraftSchema,
	type GatewayDraft,
	type IframeGatewayStatus,
	type IframeGatewayView,
	listGateways,
	patchGateway,
	removeFromList,
	replaceInList,
	sortGateways,
	statusBadgeClass,
	STATUS_LABELS,
} from "./api";

// ---------------------------------------------------------------------------
// Brand tokens
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:bg-zinc-50 disabled:text-zinc-500";
const HINT = "text-xs text-zinc-500";
const ERROR_TEXT = "text-xs text-red-600";
const BTN_PRIMARY =
	"inline-flex items-center justify-center rounded-[6px] border border-emerald-600 bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_SECONDARY =
	"inline-flex items-center justify-center rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"inline-flex items-center justify-center rounded-[6px] border border-red-300 bg-white px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";
const HEADLINE =
	"bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-transparent";

// ---------------------------------------------------------------------------
// Props — optional initialAdvertiserId for deep-link + tests.
// ---------------------------------------------------------------------------

export interface IframeGatewayPageProps {
	initialAdvertiserId?: string;
	apiOrigin?: string;
}

function defaultApiOrigin(): string {
	if (typeof window !== "undefined" && window.location?.origin) {
		return window.location.origin.replace(/\/+$/, "");
	}
	return "http://localhost:8787";
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function IframeGatewayPage(props: IframeGatewayPageProps = {}) {
	const apiOrigin = props.apiOrigin ?? defaultApiOrigin();

	const [scope, setScope] = useState<string>(props.initialAdvertiserId ?? "");
	const [scopeDraft, setScopeDraft] = useState<string>(
		props.initialAdvertiserId ?? "",
	);
	const [gateways, setGateways] = useState<IframeGatewayView[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [listError, setListError] = useState<string | null>(null);

	const [draft, setDraft] = useState<GatewayDraft>({
		...EMPTY_DRAFT,
		advertiserId: props.initialAdvertiserId ?? "",
	});
	const [draftErrors, setDraftErrors] = useState<Record<string, string>>({});
	const [submitting, setSubmitting] = useState<boolean>(false);
	const [submitError, setSubmitError] = useState<string | null>(null);

	const [actionId, setActionId] = useState<string | null>(null);
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const [notice, setNotice] = useState<string | null>(null);

	// ---------------------------------------------------------------------
	// Data hydration
	// ---------------------------------------------------------------------

	const reload = useCallback(async () => {
		setLoading(true);
		setListError(null);
		try {
			const rows = await listGateways(scope.trim() || undefined);
			setGateways(sortGateways(rows));
		} catch (err) {
			setListError(
				err instanceof Error ? err.message : "Failed to load gateways.",
			);
		} finally {
			setLoading(false);
		}
	}, [scope]);

	useEffect(() => {
		void reload();
	}, [reload]);

	const kpis = useMemo(() => countByStatus(gateways), [gateways]);
	const sorted = useMemo(() => sortGateways(gateways), [gateways]);
	const selected = useMemo(
		() => sorted.find((row) => row.id === selectedId) ?? null,
		[sorted, selectedId],
	);

	// ---------------------------------------------------------------------
	// Handlers
	// ---------------------------------------------------------------------

	function onScopeSubmit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setScope(scopeDraft.trim());
	}

	async function onCreate(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setSubmitError(null);
		const parsed = gatewayDraftSchema.safeParse(draft);
		if (!parsed.success) {
			setDraftErrors(parsed.errors);
			return;
		}
		setDraftErrors({});
		setSubmitting(true);
		try {
			const created = await createGateway(parsed.data);
			setGateways((prev) => sortGateways(replaceInList(prev, created)));
			setDraft({
				...EMPTY_DRAFT,
				advertiserId: parsed.data.advertiserId,
			});
			setSelectedId(created.id);
			setNotice(`Gateway "${created.name}" created.`);
		} catch (err) {
			setSubmitError(
				err instanceof Error ? err.message : "Create failed.",
			);
		} finally {
			setSubmitting(false);
		}
	}

	async function onToggleStatus(row: IframeGatewayView) {
		const nextStatus: IframeGatewayStatus =
			row.status === "active" ? "paused" : "active";
		setActionId(row.id);
		setNotice(null);
		try {
			const updated = await patchGateway(row.id, { status: nextStatus });
			setGateways((prev) => sortGateways(replaceInList(prev, updated)));
			setNotice(
				`Gateway "${updated.name}" is now ${STATUS_LABELS[updated.status].toLowerCase()}.`,
			);
		} catch (err) {
			setListError(
				err instanceof Error ? err.message : "Status change failed.",
			);
		} finally {
			setActionId(null);
		}
	}

	async function onDelete(row: IframeGatewayView) {
		setActionId(row.id);
		setNotice(null);
		try {
			const result = await deleteGateway(row.id);
			if ("deleted" in result && result.deleted) {
				setGateways((prev) => removeFromList(prev, row.id));
				if (selectedId === row.id) setSelectedId(null);
				setNotice(`Gateway "${row.name}" deleted.`);
			} else if ("archived" in result && result.archived) {
				setGateways((prev) =>
					sortGateways(replaceInList(prev, result.gateway)),
				);
				setNotice(`Gateway "${row.name}" archived.`);
			}
		} catch (err) {
			setListError(
				err instanceof Error ? err.message : "Delete failed.",
			);
		} finally {
			setActionId(null);
		}
	}

	function patchDraft<K extends keyof GatewayDraft>(
		key: K,
		value: GatewayDraft[K],
	) {
		setDraft((prev) => ({ ...prev, [key]: value }));
		if (draftErrors[key as string]) {
			setDraftErrors((prev) => {
				const next = { ...prev };
				delete next[key as string];
				return next;
			});
		}
	}

	// ---------------------------------------------------------------------
	// Render
	// ---------------------------------------------------------------------

	return (
		<main className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-10 sm:py-14">
			<header className="flex flex-col gap-2">
				<h1 className="text-3xl font-semibold tracking-tight">
					<span className={HEADLINE}>Iframe / gateway tracking</span>
				</h1>
				<p className="max-w-3xl text-sm text-zinc-600">
					Serve a tiny first-party iframe on walled-garden confirmation
					pages so conversions still reach Trackerity when the parent
					surface blocks third-party JS or outbound postbacks. Only
					origins on the allowlist can load the embed or fire the
					verify endpoint.
				</p>
			</header>

			<section className={`${CARD} p-5`}>
				<form
					onSubmit={onScopeSubmit}
					className="flex flex-col gap-3 sm:flex-row sm:items-end"
				>
					<div className="flex flex-col gap-1 sm:flex-1">
						<label className={LABEL} htmlFor="scope-advertiser">
							Scope by advertiser
						</label>
						<input
							id="scope-advertiser"
							className={INPUT}
							placeholder="adv_... (leave blank for all)"
							value={scopeDraft}
							onChange={(e) => setScopeDraft(e.target.value)}
						/>
						<span className={HINT}>
							Only gateways for this advertiser will be shown.
						</span>
					</div>
					<div className="flex gap-2">
						<button type="submit" className={BTN_PRIMARY}>
							Apply
						</button>
						<button
							type="button"
							className={BTN_SECONDARY}
							onClick={() => {
								setScopeDraft("");
								setScope("");
							}}
						>
							Clear
						</button>
					</div>
				</form>
			</section>

			<section className="grid grid-cols-1 gap-3 sm:grid-cols-3">
				<KpiCard label="Active" value={kpis.active} tone="emerald" />
				<KpiCard label="Paused" value={kpis.paused} tone="amber" />
				<KpiCard
					label="Archived"
					value={kpis.archived}
					tone="zinc"
				/>
			</section>

			<section className={`${CARD} p-5`}>
				<h2 className="mb-4 text-base font-semibold text-zinc-900">
					Create gateway
				</h2>
				<form
					onSubmit={onCreate}
					className="grid grid-cols-1 gap-4 md:grid-cols-2"
				>
					<FieldText
						id="draft-name"
						label="Name"
						hint="Shown in the table + reports."
						value={draft.name}
						onChange={(v) => patchDraft("name", v)}
						error={draftErrors.name}
					/>
					<FieldText
						id="draft-slug"
						label="Slug"
						hint="URL-safe, lowercase. Used in the embed path."
						value={draft.slug}
						onChange={(v) => patchDraft("slug", v)}
						error={draftErrors.slug}
					/>
					<FieldText
						id="draft-advertiser"
						label="Advertiser ID"
						hint="Links the gateway to an advertiser scope."
						value={draft.advertiserId}
						onChange={(v) => patchDraft("advertiserId", v)}
						error={draftErrors.advertiserId}
					/>
					<div className="flex flex-col gap-1">
						<label className={LABEL} htmlFor="draft-event-type">
							Event type
						</label>
						<select
							id="draft-event-type"
							className={INPUT}
							value={draft.eventType}
							onChange={(e) =>
								patchDraft(
									"eventType",
									e.target.value as GatewayDraft["eventType"],
								)
							}
						>
							{EVENT_TYPES.map((t) => (
								<option key={t} value={t}>
									{EVENT_TYPE_LABELS[t]}
								</option>
							))}
						</select>
						{draftErrors.eventType ? (
							<span className={ERROR_TEXT}>
								{draftErrors.eventType}
							</span>
						) : (
							<span className={HINT}>
								What the verify hit counts as.
							</span>
						)}
					</div>
					<div className="flex flex-col gap-1 md:col-span-2">
						<label
							className={LABEL}
							htmlFor="draft-allowed-origins"
						>
							Allowed parent origins
						</label>
						<textarea
							id="draft-allowed-origins"
							className={`${INPUT} min-h-[80px]`}
							placeholder="https://shop.brand.example, https://m.brand.example"
							value={draft.allowedOriginsText}
							onChange={(e) =>
								patchDraft(
									"allowedOriginsText",
									e.target.value,
								)
							}
						/>
						{draftErrors.allowedOriginsText ? (
							<span className={ERROR_TEXT}>
								{draftErrors.allowedOriginsText}
							</span>
						) : (
							<span className={HINT}>
								Comma or newline separated. Only these origins
								can load the embed or fire verify.
							</span>
						)}
					</div>
					<div className="flex items-center gap-3 md:col-span-2">
						<button
							type="submit"
							className={BTN_PRIMARY}
							disabled={submitting}
						>
							{submitting ? "Saving..." : "Create gateway"}
						</button>
						{submitError ? (
							<span className={ERROR_TEXT}>{submitError}</span>
						) : null}
					</div>
				</form>
			</section>

			{notice ? (
				<div className="rounded-[6px] border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
					{notice}
				</div>
			) : null}

			<section className={`${CARD} p-5`}>
				<div className="flex items-center justify-between">
					<h2 className="text-base font-semibold text-zinc-900">
						Gateways
					</h2>
					<button
						type="button"
						className={BTN_SECONDARY}
						onClick={() => void reload()}
					>
						Refresh
					</button>
				</div>

				{loading ? (
					<p className="py-6 text-sm text-zinc-500">Loading...</p>
				) : listError ? (
					<p className="py-6 text-sm text-red-600">{listError}</p>
				) : sorted.length === 0 ? (
					<p className="py-6 text-sm text-zinc-500">
						No gateways yet. Create one above to get started.
					</p>
				) : (
					<div className="mt-3 overflow-x-auto">
						<table className="min-w-full text-left text-sm">
							<thead className="border-b border-zinc-200 text-xs uppercase tracking-wide text-zinc-500">
								<tr>
									<th className="py-2 pr-3 font-medium">
										Slug
									</th>
									<th className="py-2 pr-3 font-medium">
										Advertiser
									</th>
									<th className="py-2 pr-3 font-medium">
										Event
									</th>
									<th className="py-2 pr-3 font-medium">
										Origins
									</th>
									<th className="py-2 pr-3 font-medium">
										Embeds
									</th>
									<th className="py-2 pr-3 font-medium">
										Conversions
									</th>
									<th className="py-2 pr-3 font-medium">
										Rate
									</th>
									<th className="py-2 pr-3 font-medium">
										Last fired
									</th>
									<th className="py-2 pr-3 font-medium">
										Status
									</th>
									<th className="py-2 pr-3 font-medium">
										Actions
									</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-100">
								{sorted.map((row) => (
									<tr
										key={row.id}
										className={
											row.id === selectedId
												? "bg-emerald-50/40"
												: undefined
										}
									>
										<td className="py-2 pr-3 font-mono text-xs text-zinc-800">
											{row.slug}
										</td>
										<td className="py-2 pr-3 text-zinc-700">
											{row.advertiser_id}
										</td>
										<td className="py-2 pr-3 text-zinc-700">
											{EVENT_TYPE_LABELS[row.event_type]}
										</td>
										<td className="py-2 pr-3 text-zinc-700">
											{row.allowed_origins.length}
										</td>
										<td className="py-2 pr-3 tabular-nums text-zinc-700">
											{row.embed_count.toLocaleString()}
										</td>
										<td className="py-2 pr-3 tabular-nums text-zinc-700">
											{row.conversion_count.toLocaleString()}
										</td>
										<td className="py-2 pr-3 tabular-nums text-zinc-700">
											{formatConversionRate(row)}
										</td>
										<td className="py-2 pr-3 text-zinc-500">
											{formatTimestamp(row.last_fired_at)}
										</td>
										<td className="py-2 pr-3">
											<span
												className={`inline-flex items-center rounded-[6px] border px-2 py-0.5 text-xs font-medium ${statusBadgeClass(row.status)}`}
											>
												{STATUS_LABELS[row.status]}
											</span>
										</td>
										<td className="py-2 pr-3">
											<div className="flex flex-wrap gap-1">
												<button
													type="button"
													className={BTN_SECONDARY}
													onClick={() =>
														setSelectedId(
															row.id === selectedId
																? null
																: row.id,
														)
													}
												>
													{row.id === selectedId
														? "Hide embed"
														: "Show embed"}
												</button>
												{row.status !== "archived" ? (
													<button
														type="button"
														className={BTN_SECONDARY}
														onClick={() =>
															void onToggleStatus(row)
														}
														disabled={
															actionId === row.id
														}
													>
														{row.status === "active"
															? "Pause"
															: "Activate"}
													</button>
												) : null}
												<button
													type="button"
													className={BTN_DANGER}
													onClick={() =>
														void onDelete(row)
													}
													disabled={
														actionId === row.id
													}
												>
													Delete
												</button>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</section>

			{selected ? (
				<section className={`${CARD} p-5`}>
					<h2 className="text-base font-semibold text-zinc-900">
						Embed snippet
					</h2>
					<p className={`mt-1 ${HINT}`}>
						Paste this markup onto the walled-garden confirmation
						page. Replace the query string parameters with your own
						order / revenue values before publishing.
					</p>
					<div className="mt-3 flex flex-col gap-3">
						<div>
							<div className={LABEL}>Embed URL</div>
							<code className="mt-1 block break-all rounded-[6px] border border-zinc-200 bg-zinc-50 p-3 font-mono text-xs text-zinc-800">
								{embedUrl(apiOrigin, selected)}
							</code>
						</div>
						<div>
							<div className={LABEL}>iframe tag</div>
							<code className="mt-1 block whitespace-pre-wrap break-all rounded-[6px] border border-zinc-200 bg-zinc-50 p-3 font-mono text-xs text-zinc-800">
								{embedSnippet(apiOrigin, selected)}
							</code>
						</div>
						<div className="flex flex-wrap gap-2 text-xs text-zinc-600">
							<span>
								Allowed origins: {selected.allowed_origins.length}
							</span>
							<span aria-hidden>|</span>
							<span>Status: {STATUS_LABELS[selected.status]}</span>
							<span aria-hidden>|</span>
							<span>
								Event: {EVENT_TYPE_LABELS[selected.event_type]}
							</span>
						</div>
					</div>
				</section>
			) : null}
		</main>
	);
}

// ---------------------------------------------------------------------------
// Small presentational helpers — kept inline so the owned-file surface stays
// minimal. They're exported only for tests, if needed.
// ---------------------------------------------------------------------------

interface KpiCardProps {
	label: string;
	value: number;
	tone: "emerald" | "amber" | "zinc";
}

function KpiCard({ label, value, tone }: KpiCardProps) {
	const toneClass =
		tone === "emerald"
			? "text-emerald-700"
			: tone === "amber"
				? "text-amber-700"
				: "text-zinc-700";
	return (
		<div className={`${CARD} p-4`}>
			<div className="text-xs font-medium uppercase tracking-wide text-zinc-500">
				{label}
			</div>
			<div
				className={`mt-1 text-2xl font-semibold tabular-nums ${toneClass}`}
			>
				{value.toLocaleString()}
			</div>
		</div>
	);
}

interface FieldTextProps {
	id: string;
	label: string;
	hint: string;
	value: string;
	onChange: (v: string) => void;
	error?: string;
}

function FieldText({
	id,
	label,
	hint,
	value,
	onChange,
	error,
}: FieldTextProps) {
	return (
		<div className="flex flex-col gap-1">
			<label className={LABEL} htmlFor={id}>
				{label}
			</label>
			<input
				id={id}
				className={INPUT}
				value={value}
				onChange={(e) => onChange(e.target.value)}
			/>
			{error ? (
				<span className={ERROR_TEXT}>{error}</span>
			) : (
				<span className={HINT}>{hint}</span>
			)}
		</div>
	);
}

export default IframeGatewayPage;
